import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import {
  AlertCircle,
  FileText,
  Loader2,
  Lock,
  RotateCw,
  ScanLine,
  Upload,
  X,
} from "lucide-react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";

/* ═══════════════════════════════════════════════════════════════════════
   FileDropzone
   src/components/ui/file-dropzone.tsx

   Four screens use this — the review wizard, Doc Compare, Field Review and
   the Label Finder — and today all four are a bare <input type="file">
   reading "No file chosen".

   The domain shapes it in one way that a generic dropzone wouldn't:

     A SCAN IS NOT AN ERROR. A born-digital PDF and a decades-old EPA scan
     are both valid inputs on different processing paths, and the reviewer
     needs to know which one they just handed over, because it changes what
     the pipeline can promise. So the accepted-file card reports the lane —
     "born-digital, text read directly" or "scan, read by OCR; anything
     uncertain escalates" — rather than just a filename and a size.

   Everything else is table stakes done properly: drag with a counter so
   child elements don't flicker the state, click-to-browse via a real
   label, paste from clipboard, per-reason validation messages, determinate
   upload progress, named analysis stages, and a retry that doesn't make
   the person find the file again.
   ═══════════════════════════════════════════════════════════════════════ */

export type DropzoneStatus =
  | "idle"
  | "uploading"
  | "analyzing"
  | "ready"
  | "error";

/** Why a file was refused. Each maps to a message that names the way out. */
export type RejectReason =
  | "type"
  | "size"
  | "count"
  | "encrypted"
  | "corrupt"
  | "network";

/** What the parser found once the file landed. Drives the card's lane copy. */
export interface FileMeta {
  /** Pages in the document. */
  pages?: number;
  /**
   * `true` when the PDF carries a text layer. `false` means image-only —
   * the OCR lane, with escalation on anything uncertain.
   */
  bornDigital?: boolean;
  /** Registration number read off the artwork, when the pipeline found one. */
  detectedReg?: string;
}

export interface AcceptedFile {
  name: string;
  size: number;
  type: string;
  meta?: FileMeta;
}

const zone = cva(
  [
    "group/zone relative flex w-full cursor-pointer flex-col items-center justify-center",
    "rounded-lg border border-dashed bg-surface text-center",
    "transition-colors duration-120 ease-out",
    // ring lands on the zone, not the visually-hidden input
    "has-[input:focus-visible]:outline has-[input:focus-visible]:outline-2",
    "has-[input:focus-visible]:outline-offset-2",
    "has-[input:focus-visible]:outline-[color:var(--brand)]",
  ],
  {
    variants: {
      state: {
        idle: "border-border-strong hover:border-brand hover:bg-brand-tint/30",
        /** A valid file is over the zone. */
        over: "border-brand bg-brand-tint",
        /** Something is over the zone that we can't take. */
        reject: "border-err bg-err-tint cursor-no-drop",
        busy: "border-border cursor-default hover:border-border",
        error: "border-err bg-err-tint/40",
      },
      compact: {
        true: "gap-2 px-5 py-4",
        false: "gap-3.5 px-5 py-8.5",
      },
    },
    defaultVariants: { state: "idle", compact: false },
  },
);

export interface FileDropzoneProps
  extends Omit<
      React.ComponentPropsWithoutRef<"div">,
      "onSelect" | "onError" | "children"
    >,
    Omit<VariantProps<typeof zone>, "state"> {
  /** Names what this slot is for: "Final Printed Label", "Approved master". */
  label: string;
  /**
   * One line about what reads best and what happens otherwise. This is where
   * the scan-escalates rule belongs — before the upload, not after it.
   */
  hint?: React.ReactNode;
  /** MIME types accepted. Default: PDF only. */
  accept?: string[];
  /** Human list for the empty state, e.g. "PDF". Derived from `accept` if omitted. */
  acceptLabel?: string;
  /** Bytes. Default 50 MB. */
  maxSize?: number;
  /** The file currently held, once accepted. */
  file?: AcceptedFile | null;
  status?: DropzoneStatus;
  /** 0–100 for `uploading`. Omit for an indeterminate bar. */
  progress?: number;
  /** Named stage for `analyzing` — "Reading the text layer", "Matching the master". */
  stage?: string;
  /** Set with `status="error"`. Drives which message and which way out. */
  reason?: RejectReason;
  /** Overrides the built-in message for `reason`. */
  errorMessage?: React.ReactNode;
  /** Accept a file. Validation has already passed. */
  onSelect?: (file: File) => void;
  /** Remove the held file and return to idle. */
  onRemove?: () => void;
  /** Retry after a network failure — the file is still in hand. */
  onRetry?: () => void;
  /** Paste a file from the clipboard while the zone has focus. Default on. */
  enablePaste?: boolean;
  disabled?: boolean;
}

const REASON_COPY: Record<RejectReason, { title: string; body: string }> = {
  type: {
    title: "That file type isn’t accepted here",
    body: "Upload a PDF of the label as printed.",
  },
  size: {
    title: "That file is too large",
    body: "The limit is 50 MB. Export the artwork at print resolution rather than as flattened images.",
  },
  count: {
    title: "One file at a time",
    body: "This slot takes a single document. Drop the other one in its own slot.",
  },
  encrypted: {
    title: "That PDF is password-protected",
    body: "The text can’t be read while it’s locked. Re-export it without a password.",
  },
  corrupt: {
    title: "That PDF couldn’t be opened",
    body: "It may be truncated. Try re-exporting it from the original artwork.",
  },
  network: {
    title: "The upload didn’t finish",
    body: "The file is still here — retry without picking it again.",
  },
};

export const FileDropzone = React.forwardRef<HTMLDivElement, FileDropzoneProps>(
  function FileDropzone(
    {
      className,
      label,
      hint,
      accept = ["application/pdf"],
      acceptLabel,
      maxSize = 50 * 1024 * 1024,
      file,
      status = "idle",
      progress,
      stage,
      reason,
      errorMessage,
      onSelect,
      onRemove,
      onRetry,
      enablePaste = true,
      compact = false,
      disabled = false,
      ...props
    },
    ref,
  ) {
    const inputId = React.useId();
    const [drag, setDrag] = React.useState<"none" | "over" | "reject">("none");

    /* dragenter/dragleave fire for every child element, so a naive boolean
       flickers as the pointer crosses the icon or the text. Count depth. */
    const depth = React.useRef(0);

    const canTake = (f: { type: string; size: number }): RejectReason | null => {
      if (accept.length && !accept.includes(f.type)) return "type";
      if (f.size > maxSize) return "size";
      return null;
    };

    const take = (files: FileList | null) => {
      if (!files?.length || disabled) return;
      if (files.length > 1) return; // surfaced by the caller as reason="count"
      const f = files[0];
      if (canTake(f)) return;
      onSelect?.(f);
    };

    const onDragOver = (e: React.DragEvent) => {
      e.preventDefault();
      if (disabled) return;
      // during dragover the file itself is not readable; the type is
      const item = e.dataTransfer.items?.[0];
      const bad = item && accept.length && !accept.includes(item.type);
      setDrag(bad ? "reject" : "over");
    };

    const onDragEnter = (e: React.DragEvent) => {
      e.preventDefault();
      depth.current += 1;
    };

    const onDragLeave = (e: React.DragEvent) => {
      e.preventDefault();
      depth.current -= 1;
      if (depth.current <= 0) {
        depth.current = 0;
        setDrag("none");
      }
    };

    const onDrop = (e: React.DragEvent) => {
      e.preventDefault();
      depth.current = 0;
      setDrag("none");
      take(e.dataTransfer.files);
    };

    const onPaste = (e: React.ClipboardEvent) => {
      if (!enablePaste || disabled) return;
      const f = e.clipboardData.files?.[0];
      if (f) {
        e.preventDefault();
        take(e.clipboardData.files);
      }
    };

    const busy = status === "uploading" || status === "analyzing";
    const zoneState =
      status === "error"
        ? "error"
        : busy
          ? "busy"
          : drag === "over"
            ? "over"
            : drag === "reject"
              ? "reject"
              : "idle";

    /* ── the file is held: show the card, not the zone ─────────────── */
    if (file && (status === "ready" || busy || status === "error")) {
      return (
        <div ref={ref} className={cn("flex flex-col gap-2", className)} {...props}>
          <FileCard
            label={label}
            file={file}
            status={status}
            progress={progress}
            stage={stage}
            reason={reason}
            errorMessage={errorMessage}
            onRemove={onRemove}
            onRetry={onRetry}
          />
          {hint && status === "idle" && (
            <p className="text-caption text-text-3">{hint}</p>
          )}
        </div>
      );
    }

    /* ── empty, or refused before anything was held ────────────────── */
    return (
      <div ref={ref} className={cn("flex flex-col gap-2", className)} {...props}>
        <label
          htmlFor={inputId}
          className={cn(zone({ state: zoneState, compact }), disabled && "pointer-events-none opacity-60")}
          onDragOver={onDragOver}
          onDragEnter={onDragEnter}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
          onPaste={onPaste}
        >
          <input
            id={inputId}
            type="file"
            accept={accept.join(",")}
            disabled={disabled}
            className="sr-only"
            onChange={(e) => {
              take(e.target.files);
              // allow re-picking the same file after a remove
              e.target.value = "";
            }}
          />

          {status === "error" ? (
            <ZoneError reason={reason} message={errorMessage} onRetry={onRetry} />
          ) : (
            <>
              <span
                className={cn(
                  "grid place-items-center rounded-full transition-colors duration-120",
                  compact ? "size-8" : "size-11",
                  drag === "reject"
                    ? "bg-err-tint text-err"
                    : "bg-surface-sunken text-text-3 group-hover/zone:bg-brand-tint group-hover/zone:text-brand",
                )}
              >
                <Upload className={compact ? "size-4" : "size-5"} aria-hidden="true" />
              </span>

              <span className="flex flex-col gap-1">
                <span className="text-body font-semibold text-text">
                  {drag === "reject"
                    ? `Only ${acceptLabel ?? readableAccept(accept)} here`
                    : drag === "over"
                      ? `Drop to add the ${label.toLowerCase()}`
                      : `Drop the ${label.toLowerCase()} here`}
                </span>
                {drag === "none" && (
                  <span className="text-caption text-text-3">
                    or <span className="font-medium text-brand underline underline-offset-2">browse</span>
                    {enablePaste && " · paste with ⌘V"}
                    {" · "}
                    {acceptLabel ?? readableAccept(accept)} up to {formatBytes(maxSize, 0)}
                  </span>
                )}
              </span>
            </>
          )}
        </label>

        {hint && status !== "error" && (
          <p className="text-caption leading-normal text-text-3">{hint}</p>
        )}
      </div>
    );
  },
);

/* ═══════════════════════════════════════════════════════════════════════
   FileCard — the held file, through upload, analysis and failure.

   Reports the processing lane, not just the filename. That is the piece a
   generic uploader has no reason to carry and this product can't do
   without.
   ═══════════════════════════════════════════════════════════════════════ */

export interface FileCardProps {
  label: string;
  file: AcceptedFile;
  status?: DropzoneStatus;
  progress?: number;
  stage?: string;
  reason?: RejectReason;
  errorMessage?: React.ReactNode;
  onRemove?: () => void;
  onRetry?: () => void;
  className?: string;
}

export function FileCard({
  label,
  file,
  status = "ready",
  progress,
  stage,
  reason,
  errorMessage,
  onRemove,
  onRetry,
  className,
}: FileCardProps) {
  const failed = status === "error";
  const busy = status === "uploading" || status === "analyzing";
  const scan = file.meta?.bornDigital === false;
  const locked = reason === "encrypted";

  return (
    <div
      className={cn(
        "flex items-start gap-3 rounded-lg border bg-surface p-3.5",
        failed ? "border-err bg-err-tint/40" : "border-border",
        className,
      )}
    >
      <span
        className={cn(
          "grid size-9 shrink-0 place-items-center rounded-md",
          failed
            ? "bg-err-tint text-err"
            : scan
              ? "bg-print-tint text-print"
              : "bg-master-tint text-master",
        )}
        aria-hidden="true"
      >
        {failed ? (
          locked ? <Lock className="size-4" /> : <AlertCircle className="size-4" />
        ) : scan ? (
          <ScanLine className="size-4" />
        ) : (
          <FileText className="size-4" />
        )}
      </span>

      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <span className="truncate font-id text-body-sm text-text">{file.name}</span>
          <span className="shrink-0 font-id text-micro tabular-nums text-text-3">
            {formatBytes(file.size)}
          </span>
        </div>

        {/* ── failed ─────────────────────────────────────────────── */}
        {failed && (
          <div className="mt-1.5">
            <p className="text-caption font-semibold text-err">
              {reason ? REASON_COPY[reason].title : "That file couldn’t be used"}
            </p>
            <p className="mt-0.5 max-w-[54ch] text-caption leading-normal text-text-2">
              {errorMessage ?? (reason ? REASON_COPY[reason].body : null)}
            </p>
            <div className="mt-2.5 flex items-center gap-1.5">
              {reason === "network" && onRetry && (
                <Button size="sm" leadingIcon={<RotateCw />} onClick={onRetry}>
                  Retry upload
                </Button>
              )}
              {onRemove && (
                <Button size="sm" variant="ghost" onClick={onRemove}>
                  Choose a different file
                </Button>
              )}
            </div>
          </div>
        )}

        {/* ── in flight ──────────────────────────────────────────── */}
        {busy && (
          <div className="mt-2">
            <div className="flex items-center gap-2">
              <Loader2
                className="size-3.5 shrink-0 animate-spin text-text-3 motion-reduce:animate-none"
                aria-hidden="true"
              />
              <span className="text-caption text-text-2" aria-live="polite">
                {status === "uploading"
                  ? typeof progress === "number"
                    ? `Uploading — ${Math.round(progress)}%`
                    : "Uploading…"
                  : (stage ?? "Reading the document…")}
              </span>
            </div>
            <div
              className="mt-2 h-1 overflow-hidden rounded-full bg-surface-sunken"
              role="progressbar"
              aria-valuenow={typeof progress === "number" ? Math.round(progress) : undefined}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label={`${label} upload`}
            >
              <div
                className={cn(
                  "h-full rounded-full bg-brand transition-[width] duration-260 ease-out",
                  typeof progress !== "number" && "w-1/3 animate-[indeterminate_1.4s_ease-in-out_infinite]",
                )}
                style={typeof progress === "number" ? { width: `${progress}%` } : undefined}
              />
            </div>
          </div>
        )}

        {/* ── ready — the lane, not just the metadata ────────────── */}
        {status === "ready" && (
          <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1">
            {file.meta?.pages != null && (
              <span className="font-id text-micro tabular-nums text-text-3">
                {file.meta.pages} {file.meta.pages === 1 ? "page" : "pages"}
              </span>
            )}
            {file.meta?.detectedReg && (
              <>
                <span className="text-text-3">·</span>
                <span className="font-id text-micro text-text-3">
                  EPA Reg. {file.meta.detectedReg}
                </span>
              </>
            )}
            {file.meta?.bornDigital != null && (
              <span
                className={cn(
                  "rounded-xs px-1.5 py-0.5 font-id text-micro uppercase tracking-[0.1em]",
                  scan ? "bg-print-tint text-print" : "bg-master-tint text-master",
                )}
              >
                {scan ? "Scan · OCR" : "Born-digital"}
              </span>
            )}
          </div>
        )}

        {/* The lane's consequence, stated once, where it changes expectations */}
        {status === "ready" && scan && (
          <p className="mt-1.5 max-w-[58ch] text-caption leading-normal text-text-2">
            No text layer, so this is read by OCR. Anything the reader is
            unsure of escalates to a specialist rather than being guessed at.
          </p>
        )}
      </div>

      {!busy && onRemove && !failed && (
        <IconButton
          size="sm"
          variant="ghost"
          label={`Remove ${file.name}`}
          icon={<X />}
          onClick={onRemove}
        />
      )}
    </div>
  );
}

/* ── zone-level error, when nothing was ever held ──────────────────── */

function ZoneError({
  reason,
  message,
  onRetry,
}: {
  reason?: RejectReason;
  message?: React.ReactNode;
  onRetry?: () => void;
}) {
  return (
    <>
      <span className="grid size-11 place-items-center rounded-full bg-err-tint text-err">
        <AlertCircle className="size-5" aria-hidden="true" />
      </span>
      <span className="flex flex-col gap-1">
        <span className="text-body font-semibold text-err">
          {reason ? REASON_COPY[reason].title : "That file couldn’t be used"}
        </span>
        <span className="max-w-[46ch] text-caption leading-normal text-text-2">
          {message ?? (reason ? REASON_COPY[reason].body : null)}
        </span>
      </span>
      {reason === "network" && onRetry && (
        <Button size="sm" leadingIcon={<RotateCw />} onClick={onRetry}>
          Retry
        </Button>
      )}
    </>
  );
}

/* ── utils ─────────────────────────────────────────────────────────── */

export function formatBytes(bytes: number, decimals = 1): string {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const units = ["B", "KB", "MB", "GB"];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(k)), units.length - 1);
  const n = bytes / Math.pow(k, i);
  return `${n.toFixed(i === 0 ? 0 : decimals)} ${units[i]}`;
}

function readableAccept(accept: string[]): string {
  const map: Record<string, string> = {
    "application/pdf": "PDF",
    "image/png": "PNG",
    "image/jpeg": "JPG",
    "image/tiff": "TIFF",
  };
  const names = [...new Set(accept.map((a) => map[a] ?? a.split("/")[1]?.toUpperCase()))];
  if (names.length === 1) return names[0];
  return `${names.slice(0, -1).join(", ")} or ${names.at(-1)}`;
}
